import { FC, useEffect } from "react";
import { Box, FormControl, makeStyles, TextField, IconButton, Typography, Select, MenuItem, ListItemText } from "@material-ui/core";
import { OnChangeEvent } from "types/general";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {
  getNextDay,
  getNextMonthNew,
  getNextWeek,
  getPeviousMonthNew,
  getPeviousWeek,
  getPreviousDay,
} from "utils/helpers";
import { yearConstantList } from "constants/constants";

interface PropsType {
  type: "year" | "month" | "week" | "date" | "week_range" | string | undefined;
  dateValue?: string | undefined;
  weekValue?: string | undefined;
  weekRangeOneValue?: string | undefined;
  weekRangeTwoValue?: string | undefined;
  monthValue?: string | undefined;
  yearValue?: string | undefined;
  dateChange?: (date: string) => any;
  weekChange?: (date: string) => any;
  weekRangeOneChange?: (date: string) => any;
  weekRangeTwoChange?: (date: string) => any;
  monthChange?: (date: string) => any;
  yearChange?: (date: string) => any;
  fullWidth?: boolean;
  showLabel?: boolean;
  hasSwitch?: boolean;
  isSmallSize?: boolean;
  keepSwitchOnMobile?: boolean;
}

export const DateFilter: FC<PropsType> = ({
  type,
  dateValue,
  weekValue,
  weekRangeOneValue,
  weekRangeTwoValue,
  monthValue,
  yearValue,
  dateChange,
  weekChange,
  weekRangeOneChange,
  weekRangeTwoChange,
  monthChange,
  yearChange,
  fullWidth,
  showLabel,
  hasSwitch,
  isSmallSize,
  keepSwitchOnMobile,
}) => {
  const classes = useStyles();

  const value = String(returnValue(type, dateValue, weekValue, monthValue));
  const currentWeekStr = weekRangeOneValue;
  const maxWeekStr = weekRangeOneValue ? getNextWeek(weekRangeOneValue, 3) : weekRangeOneValue;

  useEffect(() => {
    if (type !== "week_range" || !weekRangeOneValue || !weekRangeTwoValue || !maxWeekStr) return;
    if (weekRangeTwoValue < weekRangeOneValue || weekRangeTwoValue > maxWeekStr) {
      weekRangeTwoChange?.(maxWeekStr);
    }
  }, [type, weekRangeOneValue, weekRangeTwoValue, maxWeekStr, weekRangeTwoChange]);

  return (
    <Box className={`${classes.root} ${fullWidth ? classes.fullWidth : ""}`}>
      <FormControl fullWidth={fullWidth}>
        {showLabel && <Typography className={classes.label}>{type}</Typography>}
        {type === "week_range" ? (
          <Box className={classes.weekRangeBox}>
            <TextField
              className={isSmallSize ? classes.isSmallSizeField : ""}
              variant="outlined"
              type="week"
              value={weekRangeOneValue}
              onChange={(e: OnChangeEvent) => weekRangeOneChange?.(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={isSmallSize ? classes.isSmallSizeField : ""}
              variant="outlined"
              type="week"
              value={weekRangeTwoValue}
              onChange={(e: OnChangeEvent) => weekRangeTwoChange?.(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: currentWeekStr, // Disables past weeks
                max: maxWeekStr,     // Disables anything past 4 weeks
              }}
            />
          </Box>
        ) : !["year"].includes(String(type)) ? (
          <TextField
            className={isSmallSize ? classes.isSmallSizeField : ""}
            variant="outlined"
            type={type}
            value={value}
            onChange={(e: OnChangeEvent) =>
              returnAction(type, e.target.value, dateChange, weekChange, monthChange)
            }
            InputLabelProps={{ shrink: true }}
          />
        ) : (
          <Select
            className={isSmallSize ? classes.isSmallSizeSelectField : ""}
            variant="outlined"
            value={yearValue}
            onChange={(e: OnChangeEvent) => yearChange ? yearChange(e.target.value) : null}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 165,
                  width: 65,
                },
              },
              variant: "menu",
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              transformOrigin: { vertical: "top", horizontal: "center" },
            }}
          >
            {yearConstantList
              .map((year) => (
                <MenuItem key={year} value={year}>
                  <ListItemText primary={year} />
                </MenuItem>
              ))}
          </Select>
        )}
      </FormControl>
      {hasSwitch && type !== "year" && type !== "week_range" ? (
        <Box className={`${classes.switchBtnBox} ${keepSwitchOnMobile ? "visible" : ""}`}>
          <IconButton
            data-testid="testYearSelectButton"
            color="primary"
            onClick={() =>
              returnAction(
                type,
                returnPreviousAction(type, value),
                dateChange,
                weekChange,
                monthChange,
              )
            }
            className={classes.iconBtn}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            data-testid="testYearSelectButtonTwo"
            color="primary"
            onClick={() =>
              returnAction(
                type,
                returnNextAction(type, value),
                dateChange,
                weekChange,
                monthChange,
              )
            }
            className={classes.iconBtn}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      ) : (
        ""
      )}
    </Box>
  );
};

const returnValue = (
  type: string | undefined,
  dateValue: string | undefined,
  weekValue: string | undefined,
  monthValue: string | undefined,
) => {
  switch (type) {
    case "date":
      return dateValue;
    case "week":
      return weekValue;
    case "month":
      return monthValue;
    default:
      break;
  }
};

const returnAction = (
  type: string | undefined,
  value: string | undefined,
  dateChange: any,
  weekChange: any,
  monthChange: any,
) => {
  switch (type) {
    case "date":
      return dateChange(value);
    case "week":
      return weekChange(value);
    case "month":
      return monthChange(value);
    default:
      break;
  }
};
const returnPreviousAction = (type: string | undefined, value: string) => {
  switch (type) {
    case "date":
      return getPreviousDay(value, 1);
    case "week":
      return getPeviousWeek(value, 1);
    case "month":
      return getPeviousMonthNew(value, 1);
    default:
      break;
  }
};
const returnNextAction = (type: string | undefined, value: string) => {
  switch (type) {
    case "date":
      return getNextDay(value, 1);
    case "week":
      return getNextWeek(value, 1);
    case "month":
      return getNextMonthNew(value, 1);
    default:
      break;
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "flex-end",
  },
  fullWidth: {
    width: "100%",
  },
  weekRangeBox: {
    display: "flex",
    gap: 8,
  },
  isSmallSizeField: {
    "& .MuiInputBase-input": {
      fontSize: 14,
      padding: "8px 12px",
    }
  },
  isSmallSizeSelectField: {
    "& .MuiInputBase-input": {
      fontSize: 14,
      padding: "8px 32px 8px 12px",
    }
  },
  label: {
    color: "#5F6164",
    fontSize: 14,
    marginBottom: 4,
    textTransform: "capitalize",
  },
  yearInput: {
    padding: theme.spacing(1, 0),
  },
  switchBtnBox: {
    display: "flex",
  },
  iconBtn: {
    borderRadius: 0,
    padding: theme.spacing(1),
  },
  "@media (max-width: 840px)": {
    switchBtnBox: {
      "&.MuiBox-root:not(.visible)": {
        display: "none",
      },
    },
  },
}));
